import { AuthError } from '@azure/msal-browser';
import { datadogLogs } from '@datadog/browser-logs';
import * as Sentry from '@sentry/browser';
import { isBindUsingOIDC } from 'routes/LoginPage/sso/constants/oidc';

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

type SSOErrorDetailsT = {
    name?: string;
    message?: string;
    errorCode?: string;
    errorMessage?: string;
    subError?: string;
    correlationId?: string;
    cause?: string;
};

export const getSSOErrorDetails = (error: unknown): SSOErrorDetailsT => {
    if (!isObjectRecord(error)) {
        return { name: 'unknown error', cause: JSON.stringify(error) };
    }

    const { cause } = error;
    const errorDetails: SSOErrorDetailsT = {};
    if (cause instanceof Error) {
        errorDetails.cause = cause.message;
    } else if (typeof cause === 'string') {
        errorDetails.cause = cause;
    }

    if (error instanceof Error) {
        errorDetails.name = error.name;
        errorDetails.message = error.message;
    }

    if (error instanceof AuthError) {
        errorDetails.errorCode = error.errorCode;
        errorDetails.errorMessage = error.errorMessage;
        errorDetails.subError = error.subError;
        errorDetails.correlationId = error.correlationId;
    }

    return errorDetails;
};

/**
 * Temporary logger for SSO stages, only logs for the 'ampmetropole' and 'cove' bind.
 */
export const tempLogSSOStage = ({
    bind,
    attemptId,
    stage,
    durationMs,
    status,
    errorCode,
    correlationId,
    level = 'info',
}: {
    bind: string;
    attemptId: string;
    stage: string;
    durationMs?: number;
    status?: number | string;
    errorCode?: string;
    correlationId?: string;
    level?: 'info' | 'warn';
}) => {
    if (bind !== 'ampmetropole' && bind !== 'cove') return;

    datadogLogs.logger[level]('SSO login stage', {
        bind,
        attemptId,
        stage,
        durationMs,
        status,
        errorCode,
        correlationId,
    });
};

const getLikelyOIDCFailureHint = (
    errorDetails: ReturnType<typeof getSSOErrorDetails>,
) => {
    const serializedError = [
        errorDetails.errorCode,
        errorDetails.errorMessage,
        errorDetails.message,
        errorDetails.subError,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (serializedError.includes('post_request_failed')) {
        return 'The token endpoint request failed before MSAL received a response. Common causes are CORS/preflight rejection, issuer outage, DNS/network issues, mixed-content blocking, ad blockers, or a wrong issuer URL.';
    }

    if (serializedError.includes('timed_out')) {
        return 'The popup or redirect flow timed out before the identity provider completed the authentication.';
    }

    if (serializedError.includes('invalid_client')) {
        return 'The OIDC client configuration may be invalid or no longer match the identity provider registration.';
    }

    return undefined;
};

export const captureSSOLoginError = ({
    bind,
    error,
    stage,
    status,
    response,
    attemptId,
}: {
    bind: string;
    error: unknown;
    stage: 'msal' | 'manty_token_exchange';
    status?: number | string;
    response?: unknown;
    attemptId?: string;
}) => {
    const provider = isBindUsingOIDC(bind) ? 'oidc' : 'azure';
    const errorDetails = getSSOErrorDetails(error);
    const capturedError =
        error instanceof Error
            ? error
            : new Error(`User failed to login with SSO during ${stage}.`);

    Sentry.withScope((scope) => {
        scope.setTag('sso_provider', provider);
        scope.setTag('sso_stage', stage);

        if (errorDetails.errorCode) {
            scope.setTag(
                'msal_error_code',
                errorDetails.errorCode ?? 'unknown',
            );
        }

        scope.setContext('sso', {
            provider,
            stage,
            attemptId,
            userAgent: navigator.userAgent,
        });

        scope.setContext('sso_error', {
            ...errorDetails,
            likelyCause: getLikelyOIDCFailureHint(errorDetails),
        });

        if (status !== undefined || response !== undefined) {
            scope.setContext('manty_api', {
                status,
                response,
            });
        }

        Sentry.captureException(capturedError);
    });
};
